// shared/items — secret: item document schema, durability, slot taxonomy, serialization.

import { z } from 'zod';
import { TierSchema, EnchantSchema, QualitySchema } from '../tiers/index.js';

export const EquipSlotSchema = z.enum([
  'mainHand',
  'offHand',
  'head',
  'chest',
  'shoes',
  'cape',
  'consumable',
]);
export type EquipSlot = z.infer<typeof EquipSlotSchema>;

export const ItemKindSchema = z.enum([
  'weapon',
  'armor',
  'accessory',
  'resource',
  'refined',
  'tool',
  'consumable',
]);
export type ItemKind = z.infer<typeof ItemKindSchema>;

export const ItemSchema = z.object({
  id: z.string(),
  defId: z.string(),
  kind: ItemKindSchema,
  tier: TierSchema,
  enchant: EnchantSchema,
  quality: QualitySchema,
  slot: EquipSlotSchema.nullable(),
  durabilityMax: z.number().nonnegative(),
  durabilityCurrent: z.number().nonnegative(),
  stack: z.number().int().positive().default(1),
});
export type Item = z.infer<typeof ItemSchema>;

export interface ItemSpec {
  defId: string;
  kind: ItemKind;
  tier: Item['tier'];
  enchant: Item['enchant'];
  quality: Item['quality'];
  slot: EquipSlot | null;
  durabilityMax: number;
  stack?: number;
}

let idCounter = 0;
function newId(): string {
  return `item_${Date.now().toString(36)}_${(idCounter++).toString(36)}`;
}

export function create(spec: ItemSpec): Item {
  return ItemSchema.parse({
    id: newId(),
    defId: spec.defId,
    kind: spec.kind,
    tier: spec.tier,
    enchant: spec.enchant,
    quality: spec.quality,
    slot: spec.slot,
    durabilityMax: spec.durabilityMax,
    durabilityCurrent: spec.durabilityMax,
    stack: spec.stack ?? 1,
  });
}

export function damage(item: Item, hp: number): Item {
  return { ...item, durabilityCurrent: Math.max(0, item.durabilityCurrent - hp) };
}

export function isEquippable(item: Item, slot: EquipSlot): boolean {
  return item.slot === slot;
}

export function isBroken(item: Item): boolean {
  return item.durabilityCurrent <= 0;
}

import { defOf, allDefs } from './defs.js';
export { defOf, allDefs };
export type { ItemDef, StatBlock } from './defs.js';

export function createFromDef(
  defId: string,
  opts: { quality?: Item['quality']; enchant?: Item['enchant']; stack?: number } = {}
): Item {
  const def = defOf(defId);
  if (!def) throw new Error(`shared/items.createFromDef: unknown def ${defId}`);
  return create({
    defId: def.id,
    kind: def.kind,
    tier: def.tier as Item['tier'],
    enchant: opts.enchant ?? 0,
    quality: opts.quality ?? 'Normal',
    slot: def.slot,
    durabilityMax: def.durabilityMax,
    stack: opts.stack ?? 1,
  });
}
